import React, { useEffect, useRef, useState } from 'react';
import { TailwindModal } from '~/libs/ui/atoms/TailwindModal';
import { SpinningLoadingIcon } from '~/libs/ui/atoms/SpinningLoadingIcon';
import isUserProductGroupNameValid from '~/utils/UserProductGroup/isUserProductGroupNameValid';

export interface CreateFolderDialogProps {
	initialName: string;
	isCreating: boolean;
	onClose(): void;
	onSubmit(name: string): void;
}

export function CreateFolderDialog({ initialName, isCreating, onClose, onSubmit }: CreateFolderDialogProps) {
	const inputRef = useRef<HTMLInputElement>(null);
	const [name, setName] = useState(initialName);
	const [nameError, setNameError] = useState<string | null>(null);

	useEffect(() => {
		setName(initialName);
		setNameError(null);
	}, [initialName]);

	useEffect(() => {
		inputRef.current?.focus();
		inputRef.current?.select();
	}, []);

	const handleSubmit = () => {
		const trimmedName = name.trim();

		if (!isUserProductGroupNameValid(trimmedName)) {
			setNameError('Název musí mít alespoň 3 znaky.');
			return;
		}

		onSubmit(trimmedName);
	};

	return (
		<TailwindModal
			title='Vytvořit složku CZ Reimbursement'
			submitDisabled={isCreating}
			onClose={onClose}
			onSubmit={handleSubmit}
			footer={
				<>
					<button
						type='button'
						className='h-8 px-3 mr-2 rounded border border-gray-300 bg-white text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-40'
						disabled={isCreating}
						onClick={onClose}
					>
						Zrušit
					</button>
					<button
						type='submit'
						className='flex items-center gap-1.5 h-8 px-3 rounded border border-cPurple-600 bg-cPurple-600 text-sm font-semibold text-white hover:bg-cPurple-700 disabled:opacity-40'
						disabled={isCreating}
					>
						Vytvořit
						{isCreating ? <SpinningLoadingIcon className='w-3 h-3 flex-shrink-0' /> : null}
					</button>
				</>
			}
		>
			<div className='space-y-2'>
				<label className='block text-labelsmall text-gray-500 leading-tight' htmlFor='availability-create-folder-name'>
					Název složky
				</label>
				<input
					ref={inputRef}
					id='availability-create-folder-name'
					type='text'
					className={`block px-2.5 w-full h-9 border rounded text-[14px] text-black bg-white shadow ${
						nameError ? 'border-red-500' : 'border-gray-300'
					}`}
					disabled={isCreating}
					placeholder='Název složky'
					value={name}
					onChange={(event) => {
						setName(event.target.value);
						if (nameError) {
							setNameError(null);
						}
					}}
				/>
				{nameError ? <p className='text-sm text-red-600'>{nameError}</p> : null}
				<p className='text-sm text-gray-500'>
					Produkty budou přidány podle aktuálně nastaveného filtru (CZ Reimbursement). Do složky budou zahrnuty pouze
					dostupné LP.
				</p>
			</div>
		</TailwindModal>
	);
}
